/* widget/src/styles/widgetFin.module.css
 *
 * "Pearl — Messenger" variant. A second Pearl UI built to evaluate against
 * the existing widget, modelled on the Fin.ai chat:
 *   - CLOSED: a centered bottom "mini-input" launcher pill (light) that you
 *     can type into directly.
 *   - OPEN: a dark-glass panel (translucent + backdrop blur + a soft radial
 *     glow), white text, conversation growing above a pinned composer.
 *
 * Self-contained: all classnames are CSS-module hashed and every value is
 * declared here, so the variant can be reasoned about in isolation.
 */

.root {
  --fm-font: "TWK Lausanne", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, system-ui, sans-serif;
  --fm-send: #6dc3f7; /* MMY blue, matches the existing widget */
  --fm-ease: cubic-bezier(0.25, 0.1, 0.25, 1);
  /* SUMMER 2026 LIGHT THEME (Justin 2026-07-27): white paper + shadow
   * elevation + the summer theme's sky blue (#6DC3F7). Text is Deep Water
   * navy (#06243a — the skin's own navy, WCAG AAA on white). The previous
   * dark-glass values are preserved in git history for the wind-down. */
  --fm-glass: rgba(255, 255, 255, 0.94);
  --fm-glass-edge: rgba(6, 36, 58, 0.10);
  --fm-text: #06243a;
  --fm-text-soft: rgba(6, 36, 58, 0.64);
  /* Faint kept ≥0.50 alpha of navy for WCAG on white (mirrors the
   * 2026-06-11 a11y audit's floor on the dark glass). */
  --fm-text-faint: rgba(6, 36, 58, 0.52);
  --fm-user-bubble: rgba(109, 195, 247, 0.24);
  --fm-send-hover: #57b8f4;
  /* Design tokens (visual audit 2026-06-11) — radii, glass blur, shadow
   * elevations, and the shared (now light) menu glass. */
  --radius-sm: 10px; /* small controls: attach items, inner cards */
  --radius-md: 16px; /* menus, sheets, popovers */
  --radius-lg: 22px; /* panel, canvas, major surfaces */
  --blur-primary: blur(32px) saturate(150%);
  --blur-modal: blur(24px) saturate(150%);
  /* Navy-tinted elevation — on white, shadows ARE the depth system. */
  --shadow-1: 0 1px 2px rgba(6, 36, 58, 0.08), 0 4px 12px -4px rgba(6, 36, 58, 0.10);
  --shadow-2: 0 8px 32px -8px rgba(6, 36, 58, 0.18), 0 2px 8px rgba(6, 36, 58, 0.06);
  --shadow-3: 0 24px 70px -16px rgba(6, 36, 58, 0.30);
  --glass-menu: rgba(255, 255, 255, 0.97);

  position: fixed;
  inset: 0;
  z-index: 2147483000;
  pointer-events: none;
  font-family: var(--fm-font);
}
.root > * {
  pointer-events: auto;
}

/* Scrim — dims the page when the panel is open (stronger on mobile). */
.scrim {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 12, 0);
  backdrop-filter: blur(0);
  -webkit-backdrop-filter: blur(0);
  transition: background 0.4s var(--fm-ease), backdrop-filter 0.4s var(--fm-ease);
  pointer-events: none;
}
.root.open .scrim {
  /* Same dim + blur on every breakpoint so the container reads like the
   * mobile homepage everywhere (the blur is what makes the 0.78 glass look
   * solid; desktop previously lacked it). */
  background: rgba(10, 10, 12, 0.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  pointer-events: auto;
}

/* ====================== CENTERED LAUNCHER ====================== */
/* A light mini-input pill centered at the bottom of the viewport. Typing or
 * clicking opens the panel; Enter sends and opens. */
.launcher {
  position: fixed;
  left: 50%;
  bottom: max(24px, env(safe-area-inset-bottom));
  transform: translateX(-50%);
  /* The input bar — shown only when open (the chip is the closed-state
   * launcher on every breakpoint now). Sits at the bottom as a separate pill;
   * the panel floats above it. Column so an image-preview strip can stack
   * above the input row when photos are attached. */
  width: min(544px, calc(100vw - 32px));
  display: none;
  flex-direction: column;
  gap: 8px;
  background: #ffffff;
  border: 1px solid rgba(20, 22, 26, 0.08);
  border-radius: 999px;
  /* Generous left inset for the text now that the attach control moved to the
   * right (the ⋯ button beside Send). */
  padding: 6px 6px 6px 18px;
  /* Box-shadow is STATIC. The pearl-fire-pulse keyframe (3.2s loop on
   * the closed chip) was extended here earlier but reverted 2026-06-03
   * because animating box-shadow on the open input compounded with the
   * panel's bottom transition during mobile keyboard reflow and made
   * the launcher visibly bounce on subsequent keyboard opens. Static
   * weekend-equivalent shadow restored. */
  box-shadow: var(--shadow-2);
  opacity: 0;
  /* Reverted 2026-06-03 to the pre-phase2 transition list (opacity +
   * transform + bottom only). Adding box-shadow + background + border
   * + border-radius to the transition list created two regressions on
   * mobile when the soft keyboard opened:
   *   1. box-shadow transition collided with the pearl-fire-pulse
   *      keyframe (also animating box-shadow) — the launcher's halo
   *      visibly flickered and the input bounced up/down as the
   *      kb-tracked layout settled.
   *   2. Adding transitions for properties that were also re-painted
   *      on every visualViewport tick made the whole pill ease up,
   *      overshoot, then ease back — the "fly up and then back down"
   *      Justin called out. Pre-phase2 transition list is what the
   *      mobile flow was designed around; keep it that way. */
  transition: opacity 0.32s var(--fm-ease), transform 0.32s var(--fm-ease),
    bottom 0.28s var(--fm-ease);
}
/* Input pill shows ONLY when open. In preview the "Ask Pearl" chip morphs
 * into the floating chip-stack (with a "type your own" opener) — no input
 * pill — so the preview surface is the stack alone (Justin 2026-06-14). */
.root.open .launcher {
  display: flex;
  opacity: 1;
  /* Attention pulse on open removed 2026-06-03. Animating border-color
   * + box-shadow on the launcher during mobile keyboard reflow caused
   * the input to visibly bounce on subsequent keyboard opens (each
   * visualViewport resize event re-paints the launcher, which can
   * re-trigger animation state in iOS Safari). Weekend behavior had
   * no attention pulse; restored to match. */
}

/* Preview chips strip — sits above the launcher pill when proactive
 * content fires but the customer hasn't engaged yet.
 *
 * Mirrors Fin.ai's pattern (inspected via Playwright 2026-05-31): each
 * chip is its OWN self-contained pill with its own background + subtle
 * shadow. The strip itself is just a transparent flex column —
 * NO wrapper card, NO border, NO shared background. align-items: flex-start
 * keeps chips left-aligned so they don't visually anchor to the input
 * pill's centerline. The result reads as "floating suggestions" rather
 * than "Pearl popped a card."
 *
 * Animation: each chip fades + slides in with a per-chip delay so they
 * cascade in from the bottom (Fin doesn't do this but it suits us). The
 * strip itself has no animation — just its children. */
/* The single persistent launcher container. Holds the brand chip (always, at
 * the bottom) and — when proactive content fires — the suggestion chips above
 * it. Fixed + bottom-anchored + horizontally centered. A FIXED width (--stack-w)
 * is the trick that lets the lone chip self-center against it with a pure CSS
 * percentage transform (no JS measuring): see .chip-launcher --chip-x. Both
 * states share this geometry; only the chip's translateX and the presence of
 * the suggestions change — so the closed→open change is one continuous morph
 * instead of an element swap. */
.launcher-stack {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: max(20px, env(safe-area-inset-bottom));
  --stack-w: min(340px, calc(100vw - 32px));
  width: var(--stack-w);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 7px;
  z-index: 1;
  /* Container is inert; only the chip + suggestion pills re-enable pointers. */
  pointer-events: none;
}
/* Reverse-morph exit: the whole stack fades + settles back toward the chip's
 * resting spot. Runs only on dismiss (× → dismissStack), where the suggestions
 * stay mounted for the ~240ms it takes to play before teardown. */
.launcher-stack-closing {
  animation: fm-stack-out 0.24s var(--fm-ease) both;
}
@keyframes fm-stack-out {
  to {
    opacity: 0;
    transform: translateX(-50%) translateY(6px) scale(0.97);
  }
}
.preview-chip {
  font-family: var(--fm-font);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.003em;
  color: var(--fm-text);
  /* Match the conversation panel exactly: same `--fm-glass` bg + same
   * blur + same edge border, so the chips read as the same surface
   * material as Pearl's container. (Fin uses pure dark grey, but on
   * a DipYourCar page that breaks the blue-glass brand vibe.) */
  background: var(--fm-glass);
  backdrop-filter: var(--blur-primary);
  -webkit-backdrop-filter: var(--blur-primary);
  border: 1px solid var(--fm-glass-edge);
  border-radius: 20px;
  padding: 8px 16px;
  box-shadow: var(--shadow-1);
  cursor: pointer;
  pointer-events: auto;
  max-width: 100%;
  white-space: normal;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s var(--fm-ease), transform 0.1s var(--fm-ease);
  opacity: 0;
  /* Emerge OUT OF the chip: each pill STARTS pushed down toward the chip
   * (translateY(var(--from-y)), set per-chip in JSX) and shrunk, then slides up
   * into its slot. Until it clears the chip's top edge it's hidden behind the
   * opaque pill (z-index above), so it reads as being pulled out of the chip.
   * The inline stagger (chip-adjacent first) unfurls the stack from the chip
   * upward while the chip itself stretches left — one continuous morph. */
  transform-origin: bottom left;
  animation: fm-chip-emerge 0.46s cubic-bezier(0.22, 1, 0.36, 1) both;
}
/* Reverse-morph: while the stack is closing, suggestions retract back DOWN into
 * the chip (collapse + fade) instead of vanishing in place. */
.launcher-stack-closing .preview-chip {
  animation: fm-chip-retract 0.2s var(--fm-ease) forwards;
}
@keyframes fm-chip-emerge {
  from {
    opacity: 0;
    transform: translateY(var(--from-y, 32px)) scale(0.7);
  }
  60% {
    opacity: 1;
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
@keyframes fm-chip-retract {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(var(--from-y, 24px)) scale(0.7);
  }
}
.preview-chip:hover {
  /* Slightly lifted version of --fm-glass — same hue, more opaque */
  background: rgba(109, 195, 247, 0.30);
}
.preview-chip:active {
  transform: scale(0.96);
}
/* Close control for the floating-chip strip. Distinct from a suggestion chip:
 * a small circular X that sits at the top-right of the stack. MUST re-enable
 * pointer-events (the parent .preview-strip is pointer-events:none). */
.preview-dismiss {
  /* Sits just ABOVE the chips, LEFT-aligned with them (strip is
   * align-items:flex-start) — not floated off to the far right. */
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Small neat circle, 11px glyph — but a forgiving tap area via the
   * transparent border (28px total hit target on mobile). */
  width: 26px;
  height: 26px;
  touch-action: manipulation;
  z-index: 2;
  color: var(--fm-text);
  background: var(--fm-glass);
  backdrop-filter: var(--blur-primary);
  -webkit-backdrop-filter: var(--blur-primary);
  border: 1px solid var(--fm-glass-edge);
  border-radius: 999px;
  padding: 0;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: var(--shadow-1);
  opacity: 0;
  /* Fades in last, once the chips have unfurled above the chip. */
  animation: fm-preview-chip-in 0.3s var(--fm-ease) both;
  animation-delay: 320ms;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s var(--fm-ease), transform 0.1s var(--fm-ease);
}
.preview-dismiss:hover {
  background: rgba(109, 195, 247, 0.30);
}
.preview-dismiss:active {
  transform: scale(0.92);
}
.preview-dismiss svg {
  width: 11px;
  height: 11px;
  opacity: 0.85;
}
@keyframes fm-preview-chip-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.root.open .launcher:focus-within,
.root.open .launcher-has-typed {
  animation: none;
  border-color: rgba(109, 195, 247, 0.55);
  box-shadow: var(--shadow-2),
    0 0 0 3px rgba(109, 195, 247, 0.18);
}

/* Attach mode — parent pill swaps to a solid, opaque surface so the
 * upload prompt and icon buttons read clearly regardless of the page
 * behind. Previously used a semi-transparent gradient (10% blue tint) that
 * made the white prompt text invisible on mobile and looked glassy/muddy on
 * dark backgrounds. Now fully opaque: brand-blue tint at 0.08 on top of
 * pure white = a clean, readable light surface. */
.launcher-attach-mode {
  background: #ffffff;
  border-color: rgba(109, 195, 247, 0.5);
  box-shadow:
    var(--shadow-2),
    0 0 0 3px rgba(109, 195, 247, 0.18),
    0 0 24px -6px rgba(109, 195, 247, 0.32);
  /* Override the open-state attention pulse so it doesn't fight with
   * the morph pulse below. */
  animation: fm-attach-pulse 0.5s var(--fm-ease) 1 both !important;
}
@keyframes fm-attach-pulse {
  0% {
    transform: translateX(-50%) scale(1);
  }
  35% {
    transform: translateX(-50%) scale(0.985);
  }
  100% {
    transform: translateX(-50%) scale(1);
  }
}
@keyframes fm-launcher-attention {
  0% {
    border-color: rgba(20, 22, 26, 0.14);
    box-shadow: var(--shadow-2),
      0 0 0 0 rgba(109, 195, 247, 0);
  }
  40% {
    border-color: rgba(109, 195, 247, 0.65);
    box-shadow: var(--shadow-2),
      0 0 0 8px rgba(109, 195, 247, 0.22);
  }
  100% {
    border-color: rgba(20, 22, 26, 0.14);
    box-shadow: var(--shadow-2),
      0 0 0 0 rgba(109, 195, 247, 0);
  }
}
/* With attached images the pill grows into a rounded card. */
.launcher-has-media {
  border-radius: var(--radius-lg);
}
.launcher-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  width: 100%;
}
/* Image-preview strip above the composer row. */
.launcher-previews {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 4px 0 6px;
}
.preview-thumb {
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 12px;
  overflow: hidden;
  background: #eef1f4;
  flex-shrink: 0;
}
.preview-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.preview-remove {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: none;
  background: rgba(10, 12, 16, 0.72);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
}
.preview-remove svg {
  width: 11px;
  height: 11px;
}
.preview-loading {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.preview-spinner {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid rgba(20, 22, 26, 0.18);
  border-top-color: var(--fm-send);
  animation: fm-spin 0.7s linear infinite;
}
@keyframes fm-spin {
  to {
    transform: rotate(360deg);
  }
}
.launcher-error {
  color: #c0392b;
  font-size: 12px;
  line-height: 1.3;
  padding: 0 10px 2px;
}

/* Inline action inside an error banner (cart-add retry / dismiss). */
.launcher-error-retry {
  font-family: var(--fm-font);
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: rgba(6, 36, 58, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  padding: 3px 10px;
  margin-left: 6px;
  cursor: pointer;
}
.launcher-error-retry:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* "Back to chat" escape inside an empty canvas/sheet (audit 2026-06-11:
 * the empty state was a dead end that hid the conversation). */
.canvas-empty-back {
  display: inline-block;
  margin-top: 10px;
  font-family: var(--fm-font);
  font-size: 13px;
  font-weight: 600;
  color: var(--fm-text);
  background: rgba(6, 36, 58, 0.05);
  border: 1px solid rgba(150, 180, 255, 0.18);
  border-radius: 999px;
  padding: 7px 14px;
  cursor: pointer;
  pointer-events: auto;
}
.canvas-empty-back:hover {
  background: rgba(6, 36, 58, 0.08);
}
/* Visually-hidden native file input. */
.file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
/* ⋯ "more" button beside Send — opens the attach menu (Fin-style). */
.launcher-more {
  width: 38px;
  height: 38px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: #8b9097;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s var(--fm-ease), color 0.15s var(--fm-ease);
}
.launcher-more:hover {
  background: #eef1f4;
  color: #5b6168;
}
.launcher-more:active {
  transform: scale(0.93);
}
.launcher-more-open {
  background: #eef1f4;
  color: #5b6168;
}
.launcher-more svg {
  width: 20px;
  height: 20px;
}

/* Attach popover — floats above the composer, anchored to the ⋯ button. */
.attach-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1;
}

/* Attach zone — in-place morph. KEEPS the launcher pill's exact
 * footprint (same height, same border-radius, same horizontal flex
 * layout); only the inner content swaps. Compose with .launcher-row in
 * the JSX so all the row chrome carries over unchanged.
 *
 * Layout: [upload icon + prompt text] [photo] [video] [file] [close]
 * — five items in a row, just like the original textarea + 3 buttons.
 * That keeps the visual mass identical — no jarring layout shift, no
 * "card" sliding in.
 *
 * Drag-over: an animated gradient sweep around the edge — that's the
 * "trendy" part. Uses a conic-gradient mask running around the pill so
 * the border itself becomes a moving rainbow trail when a file's being
 * dragged. Falls back to a solid accent border on browsers that don't
 * support @property (Safari ≤15). */
.attach-zone {
  position: relative;
  /* Use the row's existing min height/padding — composed via cn(
   * fm.launcher-row, fm.attach-zone). gap inherits from .launcher-row.
   * Override just the bits we need different. */
}
.attach-zone-prompt {
  flex: 1;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 9px 0 9px 4px;
  font-family: var(--fm-font);
  font-size: 15px;
  font-weight: 540;
  /* Dark text — the attach bar background is now a solid opaque light
   * surface (#f0f8ff), so the former white-60% text was invisible. Use
   * the same dark tone as the launcher placeholder. */
  color: #5b6168;
  letter-spacing: -0.005em;
  user-select: none;
  /* Slide-fade in from the left so the morph reads as content moving
   * INTO the pill from where the textarea was. */
  animation: fm-attach-prompt-in 0.34s var(--fm-ease) both;
}
@keyframes fm-attach-prompt-in {
  from {
    opacity: 0;
    transform: translateX(-8px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
.attach-zone-prompt-icon {
  display: inline-flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  /* Match the solid-surface text color (see .attach-zone-prompt). */
  color: #8b9097;
  flex-shrink: 0;
}
.attach-zone-prompt-icon svg {
  width: 18px;
  height: 18px;
}
.attach-zone-prompt-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Quick-pick icon buttons — same footprint as .launcher-more so the
 * morph feels like a cousin of the regular row. Hovering tints each
 * to its corresponding type color so the customer learns the mapping
 * at a glance. */
.attach-zone-quick {
  width: 38px;
  height: 38px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: #8b9097;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s var(--fm-ease), color 0.15s var(--fm-ease),
    transform 0.1s var(--fm-ease);
  /* Cascade in from the right (opposite to the prompt's left-slide) so
   * the morph feels like two pieces meeting in the middle. Each quick
   * picks up a small delay relative to the next so they enter in
   * sequence, not all at once. */
  animation: fm-attach-quick-in 0.34s var(--fm-ease) both;
}
.attach-zone-quick:nth-of-type(1) {
  animation-delay: 80ms;
}
.attach-zone-quick:nth-of-type(2) {
  animation-delay: 140ms;
}
.attach-zone-quick:nth-of-type(3) {
  animation-delay: 200ms;
}
@keyframes fm-attach-quick-in {
  from {
    opacity: 0;
    transform: translateX(8px) scale(0.85);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}
.attach-zone-quick:hover {
  background: rgba(6, 36, 58, 0.05);
  color: #fff;
}
.attach-zone-quick:active {
  transform: scale(0.93);
}
.attach-zone-quick svg {
  width: 19px;
  height: 19px;
}
/* The three quick-picks pick up their accent on hover, in order. Color-
 * coding lifts the icon from "generic upload button" to "you know which
 * one does what." */
.attach-zone-quick:nth-of-type(1):hover {
  color: #6dc3f7;
}
.attach-zone-quick:nth-of-type(2):hover {
  color: #c7a4f5;
}
.attach-zone-quick:nth-of-type(3):hover {
  color: #6deaa3;
}

/* Drag-over visualization. Trendy animated conic-gradient sweeps round
 * the pill border. The pseudo-element is the moving gradient; mask-
 * composite cuts a thin ring inside the existing border-radius. */
@property --attach-angle {
  syntax: "<angle>";
  inherits: false;
  initial-value: 0deg;
}
.attach-zone-dragging::before {
  content: "";
  position: absolute;
  inset: -2px;
  border-radius: inherit;
  padding: 2px;
  background: conic-gradient(
    from var(--attach-angle),
    #6dc3f7,
    #c7a4f5,
    #6deaa3,
    #f59e0b,
    #6dc3f7
  );
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  animation: fm-attach-sweep 2.4s linear infinite;
  pointer-events: none;
}
@keyframes fm-attach-sweep {
  to {
    --attach-angle: 360deg;
  }
}
.attach-zone-dragging .attach-zone-prompt {
  color: var(--fm-text);
}
.attach-zone-dragging .attach-zone-prompt-icon {
  color: var(--fm-text);
  animation: fm-attach-bob 0.9s var(--fm-ease) infinite;
}
@keyframes fm-attach-bob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}
.attach-menu {
  position: absolute;
  z-index: 2;
  right: 6px;
  bottom: calc(100% + 8px);
  min-width: 184px;
  background: #ffffff;
  border: 1px solid rgba(20, 22, 26, 0.08);
  border-radius: 14px;
  padding: 6px;
  box-shadow: 0 12px 34px -10px rgba(10, 12, 16, 0.4),
    0 2px 8px rgba(10, 12, 16, 0.14);
  transform-origin: bottom right;
  animation: fm-attach-in 0.16s var(--fm-ease) backwards;
}
@keyframes fm-attach-in {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.attach-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: none;
  background: transparent;
  color: #15171a;
  font-family: var(--fm-font);
  font-size: 14px;
  font-weight: 540;
  letter-spacing: -0.005em;
  text-align: left;
  padding: 9px 10px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.14s var(--fm-ease);
}
.attach-item:hover {
  background: #eef1f4;
}
.attach-item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.attach-item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #eef1f4;
  color: #5b6168;
  flex-shrink: 0;
}
.attach-item-icon svg {
  width: 17px;
  height: 17px;
}
.launcher-input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  font-family: var(--fm-font);
  font-size: 16px;
  line-height: 1.4;
  color: #15171a;
  padding: 9px 0;
  resize: none;
  max-height: 100px;
}
/* Slightly darker + semi-bold so the placeholder reads as a clear
 * instruction ("Message Pearl…") rather than a faint suggestion. The
 * was-color #8b9097 looked decorative; #5b6168 + 500 weight reads as
 * intentional. */
.launcher-input::placeholder {
  color: #5b6168;
  font-weight: 500;
}
.launcher-send {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  border: none;
  background: #eef1f4;
  color: #5b6168;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s var(--fm-ease), color 0.15s var(--fm-ease),
    transform 0.12s var(--fm-ease);
}
.launcher-send:hover {
  background: var(--fm-send);
  color: #fff;
}
/* Blue when there's a message ready to send. */
.launcher-send-ready {
  background: var(--fm-send);
  color: #fff;
}
.launcher-send-ready:hover {
  background: var(--fm-send-hover);
}
.launcher-send:active {
  transform: scale(0.93);
}
.launcher-send svg {
  width: 18px;
  height: 18px;
}
.launcher-send:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Collapsed launcher — a brand chip (logo + label). Shown closed on every
 * breakpoint; hidden once the panel opens. */
/* Thin "live fire" glow that shimmers blue↔red. Done with box-shadow (not a
 * blurred gradient element): shadows always follow the pill's border-radius,
 * so there's no rectangular layer to leak a corner — iOS Safari does NOT clip
 * a filtered/blurred layer to border-radius, which showed a faint box before.
 * Both colors stay present (inner one strong, outer faint) and swap intensity,
 * so it shimmers without passing through a muddy purple midpoint. */
@keyframes pearl-fire-pulse {
  0%,
  100% {
    box-shadow: 0 6px 24px -8px rgba(10, 12, 16, 0.38),
      0 2px 6px rgba(10, 12, 16, 0.14),
      0 0 7px 1px rgba(109, 195, 247, 0.95),
      0 0 16px 3px rgba(255, 58, 77, 0.3);
  }
  50% {
    box-shadow: 0 6px 24px -8px rgba(10, 12, 16, 0.38),
      0 2px 6px rgba(10, 12, 16, 0.14),
      0 0 7px 1px rgba(255, 58, 77, 0.95),
      0 0 16px 3px rgba(109, 195, 247, 0.3);
  }
}

.chip-launcher {
  display: inline-flex;
  /* Flex child of .launcher-stack now (was position:fixed). Always the BOTTOM
   * child so suggestions grow above it. z-index keeps the opaque pill painting
   * OVER the emerging suggestions, so they read as sliding out from behind it. */
  position: relative;
  z-index: 2;
  align-self: flex-start;
  /* Self-center against the stack width when collapsed: `100%` resolves to the
   * chip's OWN width in a transform, so (stack-w − chipWidth)/2 lands it
   * dead-center on screen with no JS measuring. */
  --chip-x: calc((var(--stack-w) - 100%) / 2);
  transform: translateX(var(--chip-x));
  /* Stretch + counter-stretch pivot on the right edge so the pill elongates
   * LEFTWARD — into its direction of travel. */
  transform-origin: right center;
  pointer-events: auto;
  background: #ffffff;
  border: 1px solid rgba(20, 22, 26, 0.08);
  border-radius: 999px;
  /* Thin pill, like Fin's. */
  padding: 5px 14px 5px 6px;
  cursor: pointer;
  /* Static dual glow (also the prefers-reduced-motion resting state). */
  box-shadow: 0 6px 24px -8px rgba(10, 12, 16, 0.38),
    0 2px 6px rgba(10, 12, 16, 0.14),
    0 0 7px 1px rgba(109, 195, 247, 0.9),
    0 0 16px 3px rgba(255, 58, 77, 0.3);
  -webkit-tap-highlight-color: transparent;
  /* Press feedback uses the INDIVIDUAL `scale` property so it composes with the
   * morph keyframe's transform instead of overriding it. The transform
   * transition is only the re-center glide on close. */
  transition: transform 0.42s var(--fm-ease), scale 0.1s var(--fm-ease);
  animation: pearl-fire-pulse 3.2s ease-in-out infinite;
}
.chip-inner {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transform-origin: right center;
}
/* Morph (liquid stretch-then-snap, origin: right center on both layers):
 *   Phase 1 — STRETCH OVER (0→52%): translateX is HELD at --chip-x so the
 *     RIGHT edge stays pinned where the closed chip sat, while scaleX grows to
 *     ~2.05 — driving the LEFT edge across to the stack's left anchor. The
 *     content is counter-scaled (fm-chip-uncrush) so it stays full-size at the
 *     right = the trailing "tail" parked at the original spot.
 *   Phase 2 — TAIL SNAPS (52→100%): translateX rushes to 0 (right edge snaps
 *     left to join the left edge) while scaleX collapses back to 1, with a brief
 *     dip to 0.94 for an elastic settle. The tail catches up and the pill is a
 *     normal chip at the left. */
.launcher-stack-open .chip-launcher {
  animation: pearl-fire-pulse 3.2s ease-in-out infinite,
    fm-chip-stretch-left 0.66s linear forwards;
}
.launcher-stack-open .chip-inner {
  animation: fm-chip-uncrush 0.66s linear forwards;
}
@keyframes fm-chip-stretch-left {
  0% {
    transform: translateX(var(--chip-x)) scaleX(1);
    animation-timing-function: cubic-bezier(0.16, 0.84, 0.3, 1);
  }
  52% {
    transform: translateX(var(--chip-x)) scaleX(2.05);
    animation-timing-function: cubic-bezier(0.7, 0, 0.2, 1);
  }
  84% {
    transform: translateX(0) scaleX(0.94);
    animation-timing-function: cubic-bezier(0.3, 0, 0.3, 1);
  }
  100% {
    transform: translateX(0) scaleX(1);
  }
}
@keyframes fm-chip-uncrush {
  0% {
    transform: scaleX(1);
    animation-timing-function: cubic-bezier(0.16, 0.84, 0.3, 1);
  }
  52% {
    transform: scaleX(0.4878); /* 1 / 2.05 — keeps content full-size */
    animation-timing-function: cubic-bezier(0.7, 0, 0.2, 1);
  }
  84% {
    transform: scaleX(1.0638); /* 1 / 0.94 */
    animation-timing-function: cubic-bezier(0.3, 0, 0.3, 1);
  }
  100% {
    transform: scaleX(1);
  }
}
.chip-launcher:active {
  scale: 0.95;
}
@media (prefers-reduced-motion: reduce) {
  .chip-launcher {
    animation: none;
    /* No glide — the chip snaps to its stack position. */
    transition: none;
  }
  /* The closing container fades, but skip the transform settle. */
  .launcher-stack-closing {
    animation: none;
  }
  /* Chips appear without the grow/retract motion. (useProactive already skips
   * firing entirely under reduced-motion, so this is a belt-and-braces.) */
  .preview-chip,
  .launcher-stack-closing .preview-chip,
  .preview-dismiss {
    animation: none;
    opacity: 1;
    transform: none;
  }
}
.chip-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}
/* Pearlescent orb avatar — fills its round slot on the chip + header. */
.orb-svg {
  width: 100%;
  height: 100%;
  display: block;
}
.chip-label {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #15171a;
}

/* ====================== DARK-GLASS PANEL ====================== */
.panel {
  position: fixed;
  left: 50%;
  /* Floats ABOVE the input bar (input bottom 24px + ~54px height + 2px
   * gap = 80px). Tighter than original 66px → felt visually disconnected;
   * 56px brings the conversation flush against the input pill so the
   * input feels like part of the panel rather than a separate thing. */
  bottom: calc(max(24px, env(safe-area-inset-bottom)) + 56px);
  transform: translateX(-50%);
  width: min(544px, calc(100vw - 32px));
  /* Dynamic height: panel pulls UP to its content but is capped at the
   * usable viewport. Chat body still scrolls internally past the cap.
   * Caught 2026-06-03 — fixed 600px height left huge dead space below
   * short replies. min(360px ...) gives a comfortable minimum so a
   * 1-message thread doesn't look like a toast notification. */
  /* 480/640 (was 360/600) — Justin 2026-06-10: "starting height should be
   * taller on desktop". Still viewport-capped on short screens. */
  min-height: min(480px, calc(100vh - 130px));
  max-height: min(640px, calc(100vh - 130px));
  background: var(--fm-glass);
  backdrop-filter: var(--blur-primary);
  -webkit-backdrop-filter: var(--blur-primary);
  border: 1px solid var(--fm-glass-edge);
  border-radius: var(--radius-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-3);
  /* Closed: clipped down to a small centered strip at the bottom that
   * matches the launcher input's footprint. Open: expands outward (up +
   * sideways) into the full container, so the input appears to grow into
   * the panel. */
  opacity: 0;
  clip-path: inset(calc(100% - 52px) 22% 0 22% round 26px);
  pointer-events: none;
  transition: opacity 0.22s var(--fm-ease),
    clip-path 0.44s var(--fm-ease);
}
.root.open .panel {
  opacity: 1;
  clip-path: inset(0 0 0 0 round 22px);
  pointer-events: auto;
}
/* Soft radial glow behind the conversation, like the Fin panel. In
 * single-panel mode (no canvas) the glow centers in the middle of the
 * panel. When the canvas is open we re-anchor it to the panel's right
 * edge — that way the panel shows the LEFT half of the glow and the
 * canvas's matching pseudo-element (anchored to its left edge) shows
 * the RIGHT half, so together they form ONE glow centered on the seam
 * and the two halves visually merge into a single surface. */
.panel::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(
    130% 66% at 50% 34%,
    rgba(109, 195, 247, 0.30),
    rgba(109, 195, 247, 0) 62%
  );
  pointer-events: none;
}
.panel.panelWithCanvas::before {
  /* Anchor to the right edge so only the left half of the glow shows
   * inside the panel — the rest "extends" across the seam into the
   * canvas's matching pseudo-element. */
  background: radial-gradient(
    260% 100% at 100% 34%,
    rgba(109, 195, 247, 0.30),
    rgba(109, 195, 247, 0) 50%
  );
}

/* ---- Header ---- */
.header {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 14px 14px;
  /* When the canvas is open we want both header halves to read as ONE
   * unified bar. Adding the same bottom-border the canvas-header has
   * means the line runs across the full container width. With no canvas
   * (single-panel mode), the border is still subtle enough to read as
   * a normal chat-header underline rather than visual clutter. */
  border-bottom: 1px solid rgba(150, 180, 255, 0.10);
}

/* "Text us" dropdown — PANEL-level overlay (sibling of <header>, NOT a child:
 * the header's z-index:1 stacking context trapped it under the message text).
 * Mirrors the conversations .menu pattern — same anchor, glass, animation —
 * so it reads as the same material as the container. */
.textus-menu {
  position: absolute;
  top: 56px;
  right: 12px;
  z-index: 6;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: min(260px, calc(100vw - 48px));
  padding: 12px;
  background: var(--glass-menu);
  backdrop-filter: var(--blur-modal);
  -webkit-backdrop-filter: var(--blur-modal);
  border: 1px solid var(--fm-glass-edge);
  border-radius: var(--radius-md);
  box-shadow: 0 18px 48px -12px rgba(0, 0, 0, 0.6);
  transform-origin: top right;
  animation: fm-textus-in 0.18s var(--fm-ease) backwards;
}
@keyframes fm-textus-in {
  from {
    opacity: 0;
    transform: translateY(-6px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.textus-title {
  font-size: 12px;
  line-height: 1.4;
  color: rgba(6, 36, 58, 0.66);
  padding-bottom: 2px;
}
.textus-item {
  font-family: var(--fm-font);
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  text-align: left;
  background: rgba(6, 36, 58, 0.05);
  border: 1px solid rgba(150, 180, 255, 0.18);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s var(--fm-ease);
}
.textus-item:hover {
  background: rgba(6, 36, 58, 0.08);
}
.textus-item:disabled {
  opacity: 0.55;
  cursor: default;
}

.header-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}
.header-id {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.header-name {
  font-size: 15px;
  font-weight: 620;
  letter-spacing: -0.01em;
  color: var(--fm-text);
}
.header-beta {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fm-text-faint);
  border: 1px solid var(--fm-glass-edge);
  border-radius: 4px;
  padding: 1px 5px;
}
/* Question-mark icon next to "Beta" — opens the about/disclaimer sheet.
 * Smaller than the chevron menu button (compact, hugs the Beta pill). */
.header-help {
  /* 28px min hit area (a11y audit) — the 15px glyph stays centered, so the
   * visual footprint barely changes while the tap target grows. */
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  touch-action: manipulation;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: var(--fm-text-faint);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: -2px;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s var(--fm-ease), color 0.15s var(--fm-ease);
}
.header-help:hover,
.header-help-open {
  background: rgba(6, 36, 58, 0.05);
  color: var(--fm-text);
}
.header-help svg {
  width: 15px;
  height: 15px;
}
.header-close {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: var(--fm-text-soft);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s var(--fm-ease), color 0.15s var(--fm-ease);
}
.header-close:hover {
  background: rgba(6, 36, 58, 0.05);
  color: var(--fm-text);
}
.header-close svg {
  width: 19px;
  height: 19px;
}

/* Chevron trigger for the conversations menu (sits left of close). */
.header-menu {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: var(--fm-text-soft);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s var(--fm-ease), color 0.15s var(--fm-ease);
}
.header-menu:hover {
  background: rgba(6, 36, 58, 0.05);
  color: var(--fm-text);
}
.header-menu svg {
  width: 18px;
  height: 18px;
  transition: transform 0.22s var(--fm-ease);
}
.header-menu-open svg {
  transform: rotate(180deg);
}

/* ---- Conversations menu (dropdown under the header) ---- */
.menu-backdrop {
  position: absolute;
  inset: 0;
  z-index: 4;
  background: transparent;
}
.menu {
  position: absolute;
  z-index: 5;
  top: 56px;
  right: 12px;
  left: 12px;
  max-width: 320px;
  margin-left: auto;
  max-height: calc(100% - 78px);
  overflow-y: auto;
  background: var(--glass-menu);
  backdrop-filter: var(--blur-modal);
  -webkit-backdrop-filter: var(--blur-modal);
  border: 1px solid var(--fm-glass-edge);
  border-radius: var(--radius-md);
  padding: 6px;
  box-shadow: 0 18px 48px -12px rgba(0, 0, 0, 0.6);
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.18) transparent;
  transform-origin: top right;
  animation: fm-menu-in 0.18s var(--fm-ease) backwards;
}
@keyframes fm-menu-in {
  from {
    opacity: 0;
    transform: translateY(-6px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.menu-new {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  border: none;
  background: transparent;
  color: var(--fm-text);
  font-family: var(--fm-font);
  font-size: 14px;
  font-weight: 560;
  letter-spacing: -0.005em;
  text-align: left;
  padding: 10px 10px;
  border-radius: 11px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.14s var(--fm-ease);
}
.menu-new:hover {
  background: rgba(6, 36, 58, 0.05);
}
.menu-new-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--fm-send);
  color: #06243a;
  flex-shrink: 0;
}
.menu-new-icon svg {
  width: 16px;
  height: 16px;
}
.menu-label {
  font-size: 10px;
  font-weight: 650;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fm-text-faint);
  padding: 10px 10px 5px;
}
.menu-empty {
  font-size: 13px;
  color: var(--fm-text-faint);
  padding: 6px 10px 10px;
}
.menu-convo {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  border: none;
  background: transparent;
  color: var(--fm-text);
  font-family: var(--fm-font);
  text-align: left;
  padding: 9px 10px;
  border-radius: 11px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.14s var(--fm-ease);
}
.menu-convo:hover {
  background: rgba(6, 36, 58, 0.05);
}
.menu-convo-active {
  background: rgba(109, 195, 247, 0.12);
}
.menu-convo-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.07);
  color: var(--fm-text-soft);
  flex-shrink: 0;
}
.menu-convo-icon svg {
  width: 15px;
  height: 15px;
}
.menu-convo-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}
.menu-convo-preview {
  font-size: 13.5px;
  font-weight: 520;
  letter-spacing: -0.005em;
  color: var(--fm-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.menu-convo-time {
  font-size: 11.5px;
  color: var(--fm-text-faint);
}

/* ---- About-Pearl disclaimer sheet ---- */
/* Slides in from beneath the header. Same z-stack as `.menu` so backdrop
 * + sheet share the conversations-menu overlay pattern; visually wider so
 * the disclaimer copy reads as paragraphs not a column of fragments. */
.help-sheet {
  position: absolute;
  z-index: 5;
  top: 56px;
  right: 12px;
  left: 12px;
  max-height: calc(100% - 78px);
  overflow-y: auto;
  background: var(--glass-menu);
  backdrop-filter: var(--blur-modal);
  -webkit-backdrop-filter: var(--blur-modal);
  border: 1px solid var(--fm-glass-edge);
  border-radius: var(--radius-md);
  padding: 18px 18px 14px;
  box-shadow: 0 18px 48px -12px rgba(0, 0, 0, 0.6);
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.18) transparent;
  transform-origin: top center;
  animation: fm-menu-in 0.2s var(--fm-ease) backwards;
  color: var(--fm-text);
  font-family: var(--fm-font);
}
.help-title {
  font-size: 16px;
  font-weight: 620;
  letter-spacing: -0.012em;
  color: var(--fm-text);
  margin-bottom: 10px;
}
.help-lead {
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--fm-text-soft);
  margin: 0 0 14px;
}
.help-lead em {
  font-style: normal;
  font-weight: 600;
  color: var(--fm-text);
}
.help-section-label {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fm-text-faint);
  margin: 4px 0 8px;
}
.help-list {
  margin: 0 0 16px;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 9px;
}
.help-list li {
  font-size: 13px;
  line-height: 1.5;
  color: var(--fm-text-soft);
  padding-left: 14px;
  position: relative;
}
.help-list li::before {
  content: "";
  position: absolute;
  left: 2px;
  top: 8px;
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: var(--fm-text-faint);
}
.help-list strong {
  font-weight: 600;
  color: var(--fm-text);
}
.help-foot {
  font-size: 12px;
  line-height: 1.5;
  color: var(--fm-text-faint);
  margin: 4px 0 14px;
}
.help-close {
  display: block;
  width: 100%;
  border: none;
  background: rgba(6, 36, 58, 0.05);
  color: var(--fm-text);
  font-family: var(--fm-font);
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: -0.005em;
  padding: 10px 14px;
  border-radius: 12px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.14s var(--fm-ease);
}
.help-close:hover {
  background: rgba(6, 36, 58, 0.08);
}

/* ---- Message body ---- */
.body {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* Roomier bottom padding so trailing suggestion chips don't sit flush
   * against the panel's bottom edge. */
  padding: 6px 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.18) transparent;
}
.body::-webkit-scrollbar {
  width: 5px;
}
.body::-webkit-scrollbar-thumb {
  background: rgba(6, 36, 58, 0.10);
  border-radius: 3px;
}
.intro {
  font-size: 14.5px;
  line-height: 1.5;
  color: var(--fm-text);
  padding: 2px 2px 4px;
  letter-spacing: -0.005em;
}

.row {
  display: flex;
  animation: fm-in 0.3s var(--fm-ease) backwards;
}
@keyframes fm-in {
  from {
    opacity: 0;
    transform: translateY(7px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.row-assistant {
  justify-content: flex-start;
}
.row-user {
  justify-content: flex-end;
}
.bubble {
  max-width: 86%;
  font-size: 14.5px;
  line-height: 1.55;
  letter-spacing: -0.005em;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}
/* Assistant text reads near-bare on the glass (Fin style), no bubble fill. */
.bubble-assistant {
  color: var(--fm-text);
  padding: 2px 0;
}
/* User gets a soft translucent pill. */
.bubble-user {
  color: var(--fm-text);
  background: var(--fm-user-bubble);
  border: 1px solid var(--fm-glass-edge);
  border-radius: var(--radius-md);
  padding: 9px 14px;
}
/* User bubble carrying an attached photo: tighten padding, stack image + text. */
.bubble-has-media {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 6px;
}
.bubble-images {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.bubble-image {
  max-width: 200px;
  max-height: 220px;
  width: auto;
  height: auto;
  border-radius: 11px;
  display: block;
  object-fit: cover;
}
.bubble-has-media .bubble-text {
  padding: 2px 8px 4px;
}
.bubble strong {
  font-weight: 640;
}
.caret {
  display: inline-block;
  width: 0.5ch;
  margin-left: 1px;
  animation: fm-blink 0.9s steps(2, start) infinite;
}
@keyframes fm-blink {
  to {
    opacity: 0;
  }
}

/* Citation chip (inert until backend supplies sources). */
.cite {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  margin: 0 1px;
  vertical-align: 2px;
  border-radius: 5px;
  background: rgba(109, 195, 247, 0.2);
  color: #9fd8f8;
  font-size: 10px;
  font-weight: 650;
  text-decoration: none;
}

.tool-line {
  font-size: 12.5px;
  color: var(--fm-text-faint);
  padding-top: 4px;
}
.typing {
  align-self: flex-start;
  display: none;
  align-items: center;
  gap: 5px;
  background: var(--fm-user-bubble);
  border: 1px solid var(--fm-glass-edge);
  border-radius: 999px;
  padding: 10px 14px;
}
.root.thinking .typing {
  display: inline-flex;
}
.typing i {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.7);
  animation: fm-bounce 1.2s ease-in-out infinite;
}
.typing i:nth-child(2) {
  animation-delay: 0.15s;
}
.typing i:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes fm-bounce {
  0%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  50% {
    opacity: 1;
    transform: translateY(-3px);
  }
}

/* Scroll-to-bottom — floats over the foot of the panel, fades in only when
 * the user has scrolled up off the latest message. Sits above the body but
 * inside the panel's clipped bounds. */
.scroll-bottom {
  position: absolute;
  z-index: 3;
  left: 50%;
  bottom: 16px;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1px solid rgba(6, 36, 58, 0.12);
  background: #ffffff;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: #06243a;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 22px -6px rgba(6, 36, 58, 0.28);
  -webkit-tap-highlight-color: transparent;
  opacity: 0;
  transform: translateX(-50%) translateY(8px) scale(0.9);
  pointer-events: none;
  transition: opacity 0.2s var(--fm-ease), transform 0.2s var(--fm-ease);
}
.scroll-bottom-show {
  opacity: 1;
  transform: translateX(-50%) translateY(0) scale(1);
  pointer-events: auto;
}
.scroll-bottom:hover {
  background: rgba(109, 195, 247, 0.20);
}
.scroll-bottom:active {
  transform: translateX(-50%) translateY(0) scale(0.92);
}
.scroll-bottom svg {
  width: 18px;
  height: 18px;
}

/* Suggestion chips */
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  align-self: flex-start;
  max-width: 100%;
}
.chip {
  border: 1px solid var(--fm-glass-edge);
  background: rgba(255, 255, 255, 0.06);
  color: var(--fm-text);
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 13.5px;
  font-weight: 520;
  letter-spacing: -0.005em;
  cursor: pointer;
  font-family: var(--fm-font);
  transition: background 0.15s var(--fm-ease), border-color 0.15s var(--fm-ease),
    transform 0.15s var(--fm-ease);
}
.chip:hover {
  background: rgba(6, 36, 58, 0.07);
  transform: translateY(-1px);
}
.chip:active {
  transform: translateY(0) scale(0.98);
}
/* Navigation chip — used for "Take me to {product}" chips that ROUTE
 * the page instead of sending a follow-up customer message. Pearl-blue
 * background so it reads as a primary "go" action vs the soft suggestion
 * chips. Renders as an <a> so it inherits proper link semantics
 * (right-click → open in new tab still works for power users). Caught
 * 2026-06-03 — Justin asked for an explicit product-nav chip pattern. */
.chip-nav {
  background: var(--fm-send);
  border-color: var(--fm-send);
  color: #06243a;
  text-decoration: none;
  font-weight: 620;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.chip-nav:hover {
  background: #8fd2f9;
  border-color: #8fd2f9;
  color: #06243a;
}

.attachments {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
/* Product/color media always flows 2-up at a consistent size (no grow, so a
 * lone or odd-last item stays a tidy half-width square rather than a giant
 * full-width one). */
.att-media {
  flex: 0 1 calc(50% - 4px);
  min-width: 0;
  /* The reused product/color cards take their text color from --vw-ink /
   * --vw-ink-3 (near-black, for the light classic widget). On the dark-glass
   * panel that's black-on-dark and unreadable, so re-scope those tokens to
   * light here. (.att-wide gets its own light scoping below.) */
  --vw-ink: #06243a;
  --vw-ink-3: rgba(6, 36, 58, 0.62);
}
/* Square images — the shared card style crops product/color art to a squished
 * 88px strip; override to a full square so the whole image shows. */
.att-media img {
  aspect-ratio: 1 / 1;
  height: auto;
  width: 100%;
  object-fit: cover;
  display: block;
}
/* Video + everything else: full width. YouTube thumbnails keep 16:9. */
.att-video,
.att-wide {
  flex: 1 1 100%;
  min-width: 0;
}
/* Video card title was falling through to the near-black base --vw-ink
 * (black-on-dark, unreadable) — .att-media/.att-wide get re-scoped to light
 * but .att-video was missed. Same light re-scope so the title reads white. */
.att-video {
  --vw-ink: #06243a;
  --vw-ink-3: rgba(6, 36, 58, 0.62);
}
/* Tracking / discount cards on the dark panel. The order label, status and
 * meta lines read with --vw-ink/--vw-ink-3 (near-black) — unreadable here, so
 * re-scope them to light. The CTA pill uses --vw-ink as its *background*, so
 * it gets its own overridable tokens (blue fill, dark text) rather than
 * inheriting the flipped ink, which would make it white-on-white. */
.att-wide {
  --vw-ink: #06243a;
  --vw-ink-3: rgba(255, 255, 255, 0.7);
  --vw-cta-bg: var(--fm-send);
  --vw-cta-fg: #06243a;
  --vw-cta-bg-hover: #8fd2f9;
}
.att-video img {
  aspect-ratio: 16 / 9;
  height: auto;
  width: 100%;
  object-fit: cover;
  display: block;
}
/* messaging_handoff card — "Prefer to text?" white card with iMessage /
 * WhatsApp pill links. Moved here from FinMessage.tsx inline styles
 * (design-system cleanup 2026-06-11); values transcribed exactly. The 12px
 * radius is a deliberate non-token one-off (matches the prior inline value;
 * 12px was kept out of the radius scale to avoid visual change). */
.handoff-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 12px;
}
.handoff-title {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
}
.handoff-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.handoff-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  text-decoration: none;
  padding: 8px 14px;
  border-radius: 999px;
  /* The proactive card uses <button> with these classes (mint-on-click),
   * while the message attachment uses <a> — reset native button chrome so
   * both render identically. */
  border: none;
  cursor: pointer;
  font-family: inherit;
}
.handoff-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.handoff-btn-imessage {
  background: #0b93f6;
}
.handoff-btn-whatsapp {
  background: #25d366;
}
/* Proactive "Prefer to text?" card — same card, rendered in the message log
 * after the first exchange (not as a message attachment). Breathing room +
 * a quiet "Not now" dismiss. */
.handoff-card-proactive {
  margin-top: 6px;
}
.handoff-dismiss {
  margin-top: 8px;
  padding: 2px 0 0;
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  font-family: inherit;
}
.handoff-dismiss:hover {
  color: rgba(0, 0, 0, 0.7);
}

/* ---- Composer (dark translucent pill) ---- */
.composer {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  padding: 10px 14px max(12px, env(safe-area-inset-bottom));
}
.composer-pill {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  background: rgba(6, 36, 58, 0.05);
  border: 1px solid var(--fm-glass-edge);
  border-radius: var(--radius-lg);
  padding: 5px 6px 5px 16px;
  transition: border-color 0.15s var(--fm-ease);
}
.composer-pill:focus-within {
  border-color: rgba(255, 255, 255, 0.22);
}
.composer-input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  resize: none;
  font-family: var(--fm-font);
  font-size: 16px;
  line-height: 1.4;
  color: var(--fm-text);
  padding: 8px 0;
  max-height: 100px;
}
.composer-input::placeholder {
  color: var(--fm-text-faint);
}
.composer-icon {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: var(--fm-text-faint);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s var(--fm-ease), color 0.15s var(--fm-ease);
}
.composer-icon:hover {
  background: rgba(6, 36, 58, 0.05);
  color: var(--fm-text-soft);
}
.composer-icon svg {
  width: 18px;
  height: 18px;
}
.composer-send {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: none;
  background: var(--fm-send);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s var(--fm-ease), transform 0.12s var(--fm-ease),
    opacity 0.15s var(--fm-ease);
}
.composer-send:active:not(:disabled) {
  transform: scale(0.93);
}
.composer-send:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.composer-send svg {
  width: 17px;
  height: 17px;
}

/* ====================== MOBILE ====================== */
@media (max-width: 767px) {
  /* Mobile: backdrop-filter is structurally DEAD when open — the .root.open
   * visual-viewport trick puts a transform on .root, and a transformed
   * ancestor disables backdrop-filter on its fixed children (confirmed live
   * 2026-06-11: panel computed backdrop-filter:none, page text crisp through
   * the "glass"). Translucency can't be masked by blur on mobile-open at
   * all, so OPEN surfaces get SOLID paint; the glass character comes from
   * the border/shadow treatment instead. */
  /* SOLID, not dark. These were rgba(13, 21, 44, …) — the OLD dark-glass
   * navy — and survived the light-theme reskin because the reskin changed the
   * :root tokens and missed this mobile override. The result was
   * --fm-glass = dark navy while --fm-text stayed #06243a navy, i.e. navy text
   * on a navy pill: the unreadable proactive chips that got proactive selling
   * switched off entirely. Desktop was fine, so it only reproduced on phones.
   * The override exists to kill TRANSLUCENCY (backdrop-filter is structurally
   * dead on mobile-open, see above), so the light-theme equivalent is solid
   * white — same intent, correct palette. */
  .root {
    --fm-glass: #ffffff;
  }
  .root.on-product {
    --fm-glass: #ffffff;
  }
  .root.open .panel {
    background: #ffffff;
  }
  .root.open .scrim {
    background: rgba(8, 10, 16, 0.55);
  }
  .root.on-product.open .scrim {
    background: rgba(8, 10, 16, 0.75);
  }
  /* Mobile keeps the VERTICAL stack (no horizontal side-swipe — Justin
   * 2026-06-14). Widen to the phone gutter and pull it tight to the bottom,
   * matching the open composer's mobile inset so the morph anchors to the
   * same spot. The stack grows upward out of the chip; the dwell gate (6s)
   * plus the few short chips keep it from burying the buy box for long. */
  .launcher-stack {
    --stack-w: calc(100vw - 24px);
    bottom: max(14px, env(safe-area-inset-bottom));
    gap: 8px;
  }
  /* Open: size the overlay to the VISIBLE viewport (above the keyboard).
   * The `transform` makes .root the containing block for its position:fixed
   * children (.panel, .launcher), so they resolve against this overlay
   * instead of the layout viewport. The input pinned to the overlay's bottom
   * is therefore always just above the keyboard, the container stays put, and
   * iOS has nothing to scroll into view — so the page never gets pushed up.
   * useMobileSheet publishes --pearl-vvh / --pearl-vvt from visualViewport.
   * The height/top transition smooths keyboard show/hide instead of snapping;
   * it runs in lockstep with the panel `bottom` transition below so the
   * container stays pinned while the input glides up. */
  .root.open {
    inset: auto;
    top: var(--pearl-vvt, 0px);
    left: 0;
    right: 0;
    height: var(--pearl-vvh, 100%);
    transform: translateZ(0);
    transition: height 0.28s var(--fm-ease), top 0.28s var(--fm-ease);
  }
  /* Input bar — pinned to the overlay bottom (= just above the keyboard).
   * No keyboard inset needed: the overlay itself excludes the keyboard. */
  .root.open .launcher {
    width: calc(100vw - 24px);
    bottom: max(14px, env(safe-area-inset-bottom));
  }

  /* Conversation container.
   * At rest: bottom sits above the input bar, so the input is a separate pill
   * below it (a gap between them).
   * Keyboard open: subtracting --pearl-kb-inset from `bottom` cancels the
   * vv-tracked overlay's shrink, pinning the panel's SCREEN position and size.
   * The container stays put and the input floats up OVER it. */
  .panel {
    left: 8px;
    right: 8px;
    width: auto;
    transform: none;
    top: 8px;
    bottom: calc(
      max(14px, env(safe-area-inset-bottom)) + 62px - var(--pearl-kb-inset, 0px)
    );
    height: auto;
    /* Undo the desktop min/max-height clamps — on mobile the panel is
     * sized by top + bottom, so it should fill the viewport between
     * those anchors. The desktop dynamic-height rule (min(360px) /
     * max(600px)) was inheriting here and cropping the panel to 600px
     * tall on tall phones, leaving a ~120px dead band at the bottom.
     * Caught 2026-06-03 when Justin called out "you've messed with the
     * mobile container". */
    min-height: 0;
    max-height: none;
    border-radius: var(--radius-lg);
    transition: clip-path 0.44s var(--fm-ease), opacity 0.22s var(--fm-ease),
      bottom 0.28s var(--fm-ease);
  }
  /* When the keyboard floats the input up over the container, shift the
   * message list up so the latest message stays clear of it. */
  .body {
    padding-bottom: calc(18px + var(--pearl-kb-inset, 0px));
    transition: padding-bottom 0.28s var(--fm-ease);
  }
}

/* ── Mini-cart Phase 1 — canvas layout ─────────────────────────────────
 * Wraps the chat panel and the right-side canvas in a flex row. When the
 * canvas is closed (or the feature flag is off), the layout div is a
 * pass-through and the panel takes its normal full width. When the canvas
 * is open, the panel narrows to ~40% and the canvas occupies the rest.
 *
 * .layout itself is pointer-events: none so existing root-level
 * pointer-event semantics are preserved (only .panel + .canvas re-enable
 * events on themselves). On mobile (≤767px), the canvas is hidden — Phase
 * 2 will light up a bottom-sheet variant. */
.layout {
  display: flex;
  width: 100%;
  height: 100%;
  gap: 12px;
  pointer-events: none;
}
.panel {
  /* MUST re-list opacity + clip-path here: this rule comes after the base
   * .panel transition (the open/close morph) and would otherwise OVERRIDE it,
   * which is why "Pearl's open/close morph went away". Keep the open/close
   * morph (opacity + clip-path) AND the canvas reflow (width/left/transform)
   * in one transition list. */
  transition: opacity 0.22s var(--fm-ease), clip-path 0.44s var(--fm-ease),
    width 250ms var(--fm-ease), left 250ms var(--fm-ease),
    transform 250ms var(--fm-ease);
}
/* When the canvas is open, .layout itself BECOMES the unified container —
 * one fixed-position glass surface with one border, one blur, one shadow,
 * one radial glow. The panel + canvas inside are flat content panes (no
 * glass treatment of their own) split by a single vertical divider line.
 * This replaces the previous "two touching boxes" approach which still
 * read as two surfaces visually no matter how tightly they butted up. */
.layout.layout-with-canvas {
  position: fixed;
  bottom: calc(max(24px, env(safe-area-inset-bottom)) + 58px);
  /* Center-anchored: the unified container grows symmetrically out of the
   * (centered) input pill, so its visual center stays locked to the input.
   * Previously left-anchored at `50vw - 560`, which put the visual center
   * ~40px left of true center — read as "off to the left". */
  left: 50%;
  transform: translateX(-50%);
  width: calc(min(420px, 32vw) + min(620px, 48vw));
  /* Dynamic height — same model as the single .panel above. The unified
   * canvas+chat container hugs its tallest pane's content (either chat
   * messages or canvas payload) up to the max-height cap; below that it
   * shrinks. Min-height keeps a basket-only canvas from looking like a
   * floating chip. Caught 2026-06-03 per Justin's "canvas should change
   * fluidly with the contents and actually should be dynamic in size"
   * request. */
  /* 480/640 (was 360/600) — Justin 2026-06-10: "starting height should be
   * taller on desktop". Still viewport-capped on short screens. */
  min-height: min(480px, calc(100vh - 130px));
  max-height: min(640px, calc(100vh - 130px));
  background: var(--fm-glass);
  backdrop-filter: var(--blur-primary);
  -webkit-backdrop-filter: var(--blur-primary);
  border: 1px solid var(--fm-glass-edge);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-3);
  overflow: hidden;
  gap: 0;
  pointer-events: auto;
  /* Morph-in: the unified container grows from the input pill's width
   * (544px) to its full width. Uses a width keyframe rather than a
   * transition because the .layout-with-canvas class is added at the
   * SAME tick the layout becomes position:fixed — `transition` can't
   * interpolate across that, but a keyframe runs unconditionally on
   * class-add and animates from the keyframe's `from` rule. */
  animation: fm-layout-morph-in 340ms var(--fm-ease);
  /* Re-scope ink tokens so the reused build/product cards (designed for
   * the light classic widget) render with light text on the dark glass. */
  --vw-ink: #06243a;
  --vw-ink-3: rgba(6, 36, 58, 0.62);
  color: #06243a;
}
@keyframes fm-layout-morph-in {
  from {
    width: min(544px, calc(100vw - 32px));
  }
  to {
    width: calc(min(420px, 32vw) + min(620px, 48vw));
  }
}
/* Single radial blue glow across the unified container, centered just
 * above the visual midpoint. Replaces the per-half glows that used to
 * give away the split. */
.layout.layout-with-canvas::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(
    100% 66% at 50% 34%,
    rgba(109, 195, 247, 0.30),
    rgba(109, 195, 247, 0) 62%
  );
  pointer-events: none;
}
/* When inside the unified container, the panel becomes a flat content
 * pane — strip ALL its glass treatment so the unified container's glass
 * is the only one showing. Drop position:fixed and let flex layout it. */
.layout.layout-with-canvas .panel.panelWithCanvas {
  position: relative;
  left: 0;
  bottom: 0;
  transform: none;
  width: min(420px, 32vw);
  flex-shrink: 0;
  height: auto;
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  clip-path: none;
  transition: none;
  /* The panel switches position:fixed→relative when the canvas opens, which
   * can't transition and read as a "snap left then back". Mask it: fade +
   * gently settle the chat pane in sync with the container's width morph
   * (fm-layout-morph-in) so the two read as ONE smooth morph rather than a
   * jump. `both` holds the end frame; runs only on canvas-open (class add). */
  animation: fm-canvas-chat-in 320ms var(--fm-ease) both;
}
@keyframes fm-canvas-chat-in {
  from {
    opacity: 0.35;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
.layout.layout-with-canvas .panel.panelWithCanvas::before {
  display: none;
}
/* Legacy single-canvas overrides kept for the (now unused) two-box
 * fallback — left here as documentation of the previous layout in case
 * we ever need to revive it. The .layout.layout-with-canvas scope above
 * is what's actually rendering. */
.panel.panelWithCanvas {
  width: min(420px, 32vw);
  left: max(24px, calc(50vw - 560px));
  transform: none;
  border-right: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: -16px 24px 70px -16px rgba(0, 0, 0, 0.6);
}
/* Inside the unified container, the canvas is a flat content pane —
 * no glass of its own, no shadow, just a single vertical divider line
 * on its left to separate it from the chat pane. */
.layout.layout-with-canvas .canvas {
  position: relative;
  left: 0;
  bottom: 0;
  width: auto;
  flex: 1 1 auto;
  height: auto;
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border: 0;
  border-left: 1px solid rgba(150, 180, 255, 0.10);
  border-radius: 0;
  box-shadow: none;
  /* Pair with the layout morph: canvas content fades + slides in from
   * the seam (left edge of the canvas pane) so the right half appears
   * to grow OUT of the chat panel rather than pop into place. The
   * 80ms delay lets the container widen first, then the contents settle. */
  animation: fm-canvas-pane-in 280ms var(--fm-ease) 80ms both;
}
@keyframes fm-canvas-pane-in {
  from {
    opacity: 0;
    transform: translateX(-14px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
.layout.layout-with-canvas .canvas::before {
  display: none;
}
/* ── Canvas CLOSE morph (reverse of the open) ──────────────────────────
 * Driven by the `canvasClosing` state in FinChatWidget: when the canvas is
 * dismissed, `.layout-closing` is added while `.layout-with-canvas` is held
 * for one cycle (the CanvasShell stays mounted), then React unmounts once
 * these keyframes finish. The container shrinks back to the single-panel
 * width (min(544px ...)) — same width + center anchor as the base .panel —
 * so the swap to single-panel mode at unmount lands without a jump. The
 * canvas pane fades out first (faster, no delay) so it reads as collapsing
 * INTO the chat rather than the whole box just shrinking. */
.layout.layout-with-canvas.layout-closing {
  animation: fm-layout-morph-out 300ms var(--fm-ease) both;
}
@keyframes fm-layout-morph-out {
  from {
    width: calc(min(420px, 32vw) + min(620px, 48vw));
  }
  to {
    width: min(544px, calc(100vw - 32px));
  }
}
.layout.layout-with-canvas.layout-closing .canvas {
  animation: fm-canvas-pane-out 200ms var(--fm-ease) both;
}
@keyframes fm-canvas-pane-out {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-14px);
  }
}
.layout.layout-with-canvas.layout-closing .panel.panelWithCanvas {
  /* Hold the chat pane steady (and on top of the fading canvas) while the
   * container narrows around it — overriding the open keyframe so it doesn't
   * re-trigger the entrance settle during the close. */
  animation: none;
  opacity: 1;
  transform: none;
}
/* Legacy two-box canvas styles — kept for reference, no longer the
 * active layout. The .layout.layout-with-canvas .canvas scope above
 * is what renders today. */
.canvas {
  position: fixed;
  bottom: calc(max(24px, env(safe-area-inset-bottom)) + 66px);
  left: calc(max(24px, calc(50vw - 560px)) + min(420px, 32vw));
  width: min(620px, 48vw);
  height: min(600px, calc(100vh - 130px));
  background: var(--fm-glass);
  backdrop-filter: var(--blur-primary);
  -webkit-backdrop-filter: var(--blur-primary);
  border: 1px solid var(--fm-glass-edge);
  border-left: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-top-right-radius: 22px;
  border-bottom-right-radius: 22px;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 16px 24px 70px -16px rgba(0, 0, 0, 0.6);
  /* The reused build/product/color cards take their text color from
   * --vw-ink / --vw-ink-3 (near-black, for the light classic widget). On
   * the dark-glass canvas that's black-on-dark and unreadable, so re-scope
   * those tokens to light here. Mirrors the .att-media / .att-wide scoping
   * the inline-attachment list does on the panel. */
  --vw-ink: #06243a;
  --vw-ink-3: rgba(6, 36, 58, 0.62);
  color: #06243a;
  /* Match the panel's morph timing so they appear in sync. */
  transition: opacity 250ms var(--fm-ease);
}
/* Mirror of .panel.panelWithCanvas::before — anchored to the LEFT edge
 * so only the right half of the radial glow shows inside the canvas.
 * Together with the panel's right-half glow, this forms ONE continuous
 * glow centered on the seam, making the two surfaces read as a single
 * unified container instead of two adjacent boxes. */
.canvas::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(
    260% 100% at 0% 34%,
    rgba(109, 195, 247, 0.30),
    rgba(109, 195, 247, 0) 50%
  );
  pointer-events: none;
}
.canvas-header,
.canvas-body {
  position: relative;
  z-index: 1;
}
/* Canvas header — matched to .header (chat-side) in height + padding +
 * border-bottom so the two read as ONE continuous header bar across the
 * full container width, with chat branding on the left half and the
 * canvas payload title on the right half. */
.canvas-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 14px;
  /* 32px close button + 14px+14px vertical padding = 60px to match the
   * chat header (avatar 32px + 14px+14px padding = 60px). */
  min-height: 60px;
  border-bottom: 1px solid rgba(150, 180, 255, 0.10);
}
.canvas-header-title {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: #06243a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.canvas-close,
.canvas-back {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.65);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s var(--fm-ease), color 0.15s var(--fm-ease);
}
.canvas-close:hover,
.canvas-back:hover {
  background: rgba(6, 36, 58, 0.05);
  color: #06243a;
}
.canvas-close svg,
.canvas-back svg {
  width: 16px;
  height: 16px;
}
/* Back-arrow sits LEFT of the title; the close X stays on the right.
 * The canvas-header is `justify-content: space-between` already, so the
 * back button + title need to read as one cluster — pull them together
 * with a small gap and let the X float right on its own. */
.canvas-back {
  margin-right: 2px;
}
.canvas-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 14px 14px 0;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.18) transparent;
}
/* Base CTA inset — applies in the INLINE (canvas-off) chat-bubble render,
 * where the .canvas-body override below doesn't match. Without it the
 * full-width Add-to-cart button stretches edge-to-edge and touches the
 * bubble sides. The more-specific .canvas-body rule wins inside the canvas. */
[data-build-summary-cta] {
  padding: 4px 10px 8px;
}
/* Sticky Add-to-cart wrapper inside BuildSummaryCard. The card itself
 * lives inside .canvas-body (the scrolling parent), so position:sticky
 * with bottom:0 keeps the CTA pinned to the bottom edge of the canvas
 * even when the kit-contents + add-ons grid is taller than the body.
 * Width matches the cards above (no negative-margin bleed) — looks
 * like an intentional bottom-of-card action instead of a banner. */
.canvas-body [data-build-summary-cta] {
  position: sticky;
  bottom: 0;
  /* 10px horizontal margin so the white CTA button aligns with the
   * kit-content tiles in rc-body (which has 10px horizontal padding).
   * Without this, the CTA stretches to the .rc card edges and looks
   * wider than the tiles above it. */
  margin: 12px 10px 0;
  padding: 10px 0 12px;
  /* No background fill behind the CTA (the dark gradient bled visually) —
   * the sticky button sits cleanly over the canvas body. */
  background: transparent;
  z-index: 2;
}
.canvas-body::-webkit-scrollbar {
  width: 5px;
}
.canvas-body::-webkit-scrollbar-thumb {
  background: rgba(6, 36, 58, 0.10);
  border-radius: 3px;
}
.canvas-empty {
  padding: 24px 14px;
  font-size: 13px;
  text-align: center;
  color: rgba(6, 36, 58, 0.55);
}
/* Payload swap animation. Each new active payload mounts under a fresh
 * React key (payload.id), so it runs this keyframe from scratch: starts
 * 12px below + transparent and rises into place over 220ms. The prior
 * payload unmounts cleanly under it — no double-render dance needed.
 * Smooths the otherwise-abrupt content swap that made the canvas feel
 * jumpy as Pearl walked through axes / surfaced new results. */
.canvas-payload-enter {
  animation: vw-canvas-payload-in 220ms var(--fm-ease) backwards;
  /* Fill the canvas-body so children with min-height:100% (like
   * .basket-view) can pin their footers to the canvas bottom. */
  min-height: 100%;
}
@keyframes vw-canvas-payload-in {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
/* (vw-tracking-pulse keyframe is injected inline by TrackingCard so
 * CSS-module name hashing doesn't break the inline-style animation ref.) */
/* ── Basket (mini-cart) ───────────────────────────────────────────────
 * Renders inside the canvas-body when the active payload kind is
 * 'basket'. Each row shows a 56×56 product thumbnail (from the kit's
 * matte color render), title + source pill, then a price + qty stepper +
 * remove X on the right. Footer pins a subtotal + Checkout CTA. */
.basket-empty {
  padding: 32px 14px;
  text-align: center;
  color: rgba(6, 36, 58, 0.66);
}
.basket-empty-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
}
.basket-empty-sub {
  font-size: 12.5px;
  opacity: 0.7;
}
.basket-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* Fill the canvas-body so the footer (with margin-top:auto) sits at
   * the bottom of the canvas even when the customer only has 1-2 items
   * in the basket. The matching position:sticky bottom:0 on the footer
   * keeps it visible when items overflow and the body scrolls. */
  min-height: 100%;
}
.basket-view-label {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  opacity: 0.65;
  padding: 2px 2px 4px;
}
.basket-view-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.basket-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  min-width: 0;
}
.basket-row-thumb {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: var(--radius-sm);
  background-color: rgba(255, 255, 255, 0.06);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
}
.basket-row-body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.basket-row-title {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  color: #06243a;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.basket-row-source {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  opacity: 0.6;
  align-self: flex-start;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(109, 195, 247, 0.18);
  color: rgba(109, 195, 247, 0.95);
}
.basket-row-controls {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.basket-row-price {
  font-size: 13px;
  font-weight: 600;
  color: #06243a;
}
.basket-row-qty {
  display: inline-flex;
  align-items: center;
  background: rgba(6, 36, 58, 0.05);
  border-radius: 999px;
  padding: 1px;
}
.basket-row-qty button {
  /* ≥28px hit area (a11y audit) — glyph stays the same size, centered. */
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  touch-action: manipulation;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s var(--fm-ease), color 0.12s var(--fm-ease);
}
.basket-row-qty button:hover {
  background: rgba(6, 36, 58, 0.08);
  color: rgba(255, 255, 255, 1);
}
.basket-row-qty span {
  font-size: 11.5px;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
  color: #06243a;
}
.basket-row-remove {
  /* ≥28px hit area (a11y audit) — glyph stays the same size, centered. */
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  touch-action: manipulation;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: rgba(6, 36, 58, 0.55);
  cursor: pointer;
  font-size: 11px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s var(--fm-ease), color 0.12s var(--fm-ease);
}
.basket-row-remove:hover {
  background: rgba(255, 90, 90, 0.18);
  color: rgba(255, 140, 140, 1);
}
.basket-view-footer {
  position: sticky;
  bottom: 0;
  /* margin-top:auto pushes the footer to the BOTTOM of basket-view (which
   * fills the canvas-body via min-height:100%) when items don't fill the
   * pane — so Checkout is always at the bottom edge, never sitting mid-
   * canvas after a 1-item list. Horizontal 10px matches basket-row inset. */
  margin: auto 10px 0;
  padding: 10px 0 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 2;
}
.basket-view-subtotal {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 13px;
  font-weight: 600;
  color: #06243a;
}
.basket-view-subtotal span:last-child {
  font-size: 16px;
  font-weight: 700;
}
.basket-view-checkout {
  width: 100%;
  padding: 10px 14px;
  border-radius: 11px;
  border: none;
  background: #6dc3f7;
  color: #06243a;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: transform 0.12s var(--fm-ease), background 0.12s var(--fm-ease);
}
.basket-view-checkout:hover {
  background: #57b8f4;
  transform: translateY(-1px);
}

/* Canvas history tabs — the row at the bottom showing past payloads
 * the customer has parked (multi-build, prior color, etc.). One chip
 * per payload; the active one is filled, the others outlined. Hidden
 * automatically when there's only one payload. */
.history-strip {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px 10px;
  border-top: 1px solid rgba(150, 180, 255, 0.10);
  overflow-x: auto;
  scrollbar-width: none;
  position: relative;
  z-index: 1;
}
.history-strip::-webkit-scrollbar {
  display: none;
}
.history-chip {
  flex-shrink: 0;
  font: inherit;
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: -0.005em;
  padding: 5px 11px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: transparent;
  color: rgba(6, 36, 58, 0.66);
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s var(--fm-ease), color 0.15s var(--fm-ease),
    border-color 0.15s var(--fm-ease);
}
.history-chip:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #06243a;
}
.history-chip-active {
  background: rgba(109, 195, 247, 0.18);
  border-color: rgba(109, 195, 247, 0.55);
  color: #06243a;
}
@media (max-width: 767px) {
  /* Mobile: the side-by-side canvas layout collapses entirely — canvas
   * content moves to BottomSheet (FinChatWidget renders it as a separate
   * floating sheet pinned to the bottom of the viewport), and the chat
   * panel should fill the available width as if canvas-mode wasn't on.
   *
   * The selectors here must MATCH or exceed the specificity of the
   * desktop `.layout.layout-with-canvas .panel.panelWithCanvas` rule
   * (3 classes) — `@media` doesn't add specificity, so the bare
   * `.panel.panelWithCanvas` selector (2 classes) lost to the layout-
   * scoped one and the panel stayed squeezed at 32vw on mobile.
   * Caught 2026-06-03: mobile customers saw a 126px-wide panel and no
   * canvas (canvas was display:none, sheet hadn't opened yet for fresh
   * sessions). Now full-width on mobile, sheet floats below. */
  .layout.layout-with-canvas {
    /* Strip the side-by-side container — let the panel float on its
     * own (back to .panel position:fixed; mobile media rule below sets
     * the layout properties so the panel re-anchors correctly). */
    position: static;
    width: auto;
    height: auto;
    background: transparent;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border: none;
    border-radius: 0;
    box-shadow: none;
    transform: none;
    animation: none;
  }
  .layout.layout-with-canvas::before {
    display: none;
  }
  /* On mobile when canvas opens, the panel STAYS a full-screen sheet
   * (matches the .panel mobile rule above). The canvas content goes
   * into the BottomSheet — there is no "narrowed panel + side canvas"
   * layout on mobile. Earlier this rule was overriding the full-screen
   * sheet with a desktop-style 544-wide floating box, which made the
   * chat panel suddenly shrink when canvas auto-opened. Caught
   * 2026-06-03: "you've messed with the mobile container". */
  .layout.layout-with-canvas .panel.panelWithCanvas,
  .panel.panelWithCanvas {
    position: fixed;
    left: 8px;
    right: 8px;
    width: auto;
    top: 8px;
    bottom: calc(
      max(14px, env(safe-area-inset-bottom)) + 62px - var(--pearl-kb-inset, 0px)
    );
    height: auto;
    min-height: 0;
    max-height: none;
    transform: none;
    background: var(--fm-glass);
    backdrop-filter: var(--blur-primary);
    -webkit-backdrop-filter: var(--blur-primary);
    border: 1px solid var(--fm-glass-edge);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-3);
  }
  .layout.layout-with-canvas .canvas,
  .canvas {
    display: none;
  }
}

/* ── Mobile bottom sheet (Phase 2) ─────────────────────────────────────
 * Three-state sheet (peek / half / full) that takes over from CanvasShell
 * on viewports ≤767px. Lives above the composer at the bottom of the
 * viewport; the .canvas rule above is what hides the desktop variant.
 * Heights use dvh where supported (mobile Safari URL-bar friendly) with
 * vh fallback for older WebKit. */
.bottomSheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  /* SOLID: the sheet lives under the transformed .root where
   * backdrop-filter never applies (see the mobile media block) — solid
   * paint is the only reliable mask. */
  background: #ffffff;
  backdrop-filter: var(--blur-primary);
  -webkit-backdrop-filter: var(--blur-primary);
  border-top: 1px solid rgba(6, 36, 58, 0.12);
  border-radius: 18px 18px 0 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  transition: height 280ms cubic-bezier(0.32, 0.72, 0, 1);
  pointer-events: auto;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.35);
}
.bottomSheet[data-state="peek"] {
  height: 64px;
}
.bottomSheet[data-state="half"] {
  height: 60vh;
  height: 60dvh;
}
.bottomSheet[data-state="full"] {
  height: 95vh;
  height: 95dvh;
}

.bottomSheetHandle {
  width: 40px;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  margin: 8px auto 4px;
  cursor: grab;
  flex-shrink: 0;
}
.bottomSheetHandle:focus-visible {
  outline: 2px solid rgba(150, 180, 255, 0.6);
  outline-offset: 4px;
}

.bottomSheetBody {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
}

.bottomSheetPeek {
  padding: 0 16px;
  display: flex;
  align-items: center;
  height: 100%;
}

/* Basket dock chip — peek-state content rendered inside .bottomSheetPeek.
 * Full-width tappable row that shows count · subtotal (or a placeholder
 * when empty). Matches the dark-glass palette of the surrounding sheet. */
.basketDockChip {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 8px 14px;
  background: rgba(150, 180, 255, 0.08);
  border: 1px solid rgba(150, 180, 255, 0.18);
  border-radius: 12px;
  color: #fff;
  font: inherit;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 150ms ease, border-color 150ms ease;
}
.basketDockChip:hover,
.basketDockChip:focus-visible {
  background: rgba(150, 180, 255, 0.14);
  border-color: rgba(150, 180, 255, 0.28);
  outline: none;
}
.basketDockChip[data-empty] {
  opacity: 0.7;
}
.basketDockChipIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.85);
}
.basketDockChipPlaceholder {
  color: rgba(255, 255, 255, 0.65);
  font-size: 14px;
}
.basketDockChipText {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
}
.basketDockChipCount {
  color: #fff;
}
.basketDockChipDot {
  color: rgba(255, 255, 255, 0.4);
}
.basketDockChipTotal {
  color: rgba(150, 200, 255, 0.95);
  font-variant-numeric: tabular-nums;
}

/* ── Reduced motion (a11y audit 2026-06-11) ──────────────────────────────
 * Blanket kill-switch for the widget's decorative animation/transition
 * surfaces when the visitor asks for reduced motion. The orb's SVG
 * <animate> elements are already gated in JS (orbAnimated ←
 * matchMedia(prefers-reduced-motion)), and the typewriter placeholder
 * falls back to static copy in its own effect — this block covers the
 * CSS-driven rest. 0.01ms (not 0) so transitionend still fires for any
 * logic listening on it. */
@media (prefers-reduced-motion: reduce) {
  .preview-chip,
  .preview-dismiss,
  .panel,
  .layout,
  .menu,
  .textus-menu,
  .help-sheet,
  .attach-zone-dragging::before,
  .attach-zone-dragging .attach-zone-prompt-icon,
  .canvas,
  .bottomSheet {
    animation: none !important;
    transition-duration: 0.01ms !important;
  }
}

/* ---- Post-resolution CSAT prompt ----
 * Small, unobtrusive rating card that floats just above the launcher pill
 * after a meaningfully-engaged chat is closed (operator-gated — see
 * props.csatEnabled). Reuses the same blue-glass material + tokens as the
 * preview chips / panel so it reads as the same Pearl surface. */
.csat-card {
  position: fixed;
  right: 20px;
  bottom: 86px; /* clears the launcher pill below it */
  z-index: 2147483002;
  width: min(300px, calc(100vw - 32px));
  box-sizing: border-box;
  padding: 14px 16px 12px;
  background: var(--fm-glass);
  backdrop-filter: var(--blur-primary);
  -webkit-backdrop-filter: var(--blur-primary);
  border: 1px solid var(--fm-glass-edge);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-2);
  color: var(--fm-text);
  font-family: var(--fm-font);
  pointer-events: auto;
  animation: fm-csat-in 0.26s var(--fm-ease) both;
}
@keyframes fm-csat-in {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.csat-dismiss {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  background: rgba(6, 36, 58, 0.05);
  color: var(--fm-text-soft);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.14s var(--fm-ease), color 0.14s var(--fm-ease);
}
.csat-dismiss svg {
  width: 11px;
  height: 11px;
}
.csat-dismiss:hover {
  background: rgba(6, 36, 58, 0.09);
  color: var(--fm-text);
}
.csat-title {
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: -0.008em;
  color: var(--fm-text);
  margin: 0 22px 10px 0;
}
.csat-stars {
  display: flex;
  gap: 4px;
  margin-bottom: 10px;
}
.csat-star {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 2px;
  border: none;
  background: transparent;
  color: var(--fm-text-faint);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: color 0.12s var(--fm-ease), transform 0.1s var(--fm-ease);
}
.csat-star svg {
  width: 26px;
  height: 26px;
  fill: currentColor;
}
.csat-star:hover {
  transform: scale(1.12);
}
.csat-star:active {
  transform: scale(0.94);
}
.csat-star-on {
  color: var(--fm-send);
}
.csat-comment {
  width: 100%;
  box-sizing: border-box;
  font-family: var(--fm-font);
  font-size: 16px; /* iOS zoom-prevention minimum — matches the composer input */
  color: var(--fm-text);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--fm-glass-edge);
  border-radius: var(--radius-sm);
  padding: 8px 10px;
  outline: none;
}
.csat-comment::placeholder {
  color: var(--fm-text-faint);
}
.csat-comment:focus {
  border-color: rgba(150, 180, 255, 0.32);
  background: rgba(6, 36, 58, 0.06);
}
.csat-thanks {
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: -0.008em;
  color: var(--fm-text);
  margin: 2px 22px 2px 0;
  padding: 4px 0;
}


